import jwtDecode from 'jwt-decode';

import { AUTH0_LOGIN_REDIRECT, getNavinaAuth0AuthURL } from '../config/Environment';
import { getOrCreateAuthStore, type AuthStore } from '../stores/authStore';
import { NEXT_URL_QUERY_PARAMS } from '../utils/consts';

const TOKEN_EXPIRATION_BUFFER_DURATION_SECONDS = 60;

enum AuthenticationProvider {
	Cognito = 'Cognito',
	Auth0 = 'Auth0',
}
export interface GetAuthenticationInfoResponse {
	readonly headers: Record<string, string>;
	readonly baseUrlWithAuthPath: `${string}api/auth0/` | `${string}api/cognito/`;
}

function getAuthenticationProvider(authStore: AuthStore): AuthenticationProvider {
	if (authStore.getIsSAML) {
		return AuthenticationProvider.Auth0;
	}

	return AuthenticationProvider.Cognito;
}

interface IAuthenticationTokenGetter extends Pick<GetAuthenticationInfoResponse, 'baseUrlWithAuthPath'> {
	readonly headers: Record<string, string>;
}

interface AuthenticationTokenGetterParams {
	readonly authStore: AuthStore;
	readonly baseUrl: string;
}

class Auth0TokenGetter implements IAuthenticationTokenGetter {
	readonly authStore: AuthStore;
	readonly baseUrl: string;

	private constructor({ authStore, baseUrl }: AuthenticationTokenGetterParams) {
		this.authStore = authStore;
		this.baseUrl = baseUrl;
	}

	static readonly create = (params: AuthenticationTokenGetterParams) => new Auth0TokenGetter(params);

	get token(): string {
		const token = this.authStore.getToken;
		const tokenPayload = jwtDecode<{ exp: number }>(token);
		const tokenExpirationUnixTimestamp = tokenPayload.exp;

		const tokenExpirationTimeInMilliseconds = tokenExpirationUnixTimestamp * 1_000;
		const bufferDurationInMilliseconds = TOKEN_EXPIRATION_BUFFER_DURATION_SECONDS * 1_000;

		const currentTimeInMilliseconds = Date.now();

		const isTokenExpired = tokenExpirationTimeInMilliseconds - bufferDurationInMilliseconds < currentTimeInMilliseconds;

		if (isTokenExpired) {
			console.log('Token about to expire, redirecting to Auth0 to get new token', {
				currentUrl: globalThis.location.href,
			});
			// We need double encoding since it is decoded twice before used in the url
			const nextUrl = encodeURIComponent(encodeURIComponent(`${window.location.pathname}${window.location.search}`));

			console.log('Redirecting to Auth0 to get new token', { nextUrl });

			globalThis.location.href = getNavinaAuth0AuthURL(`${AUTH0_LOGIN_REDIRECT}?${NEXT_URL_QUERY_PARAMS}=${nextUrl}`);

			// TODO - make sure this error can be thrown without interrupting the globalThis.location.href redirect above
			throw new Error('Token expired, redirecting to Auth0 to get new token');
		}

		return token;
	}

	get headers(): Record<string, string> {
		return {
			authorizationToken: `Bearer ${this.token}`,
			type: 'TOKEN',
		};
	}

	get baseUrlWithAuthPath(): IAuthenticationTokenGetter['baseUrlWithAuthPath'] {
		const { baseUrl } = this;
		return `${baseUrl}api/auth0/`;
	}
}

class CognitoTokenGetter implements IAuthenticationTokenGetter {
	readonly authStore: AuthStore;
	readonly baseUrl: string;

	private constructor({ authStore, baseUrl }: AuthenticationTokenGetterParams) {
		this.authStore = authStore;
		this.baseUrl = baseUrl;
	}

	static readonly create = (params: AuthenticationTokenGetterParams) => new CognitoTokenGetter(params);
	get token(): string {
		return this.authStore.getToken;
	}

	get headers(): Record<string, string> {
		return {
			token: this.token,
		};
	}

	get baseUrlWithAuthPath(): IAuthenticationTokenGetter['baseUrlWithAuthPath'] {
		const { baseUrl } = this;
		return `${baseUrl}api/cognito/`;
	}
}

const TOKEN_GETTER_CREATORS = {
	[AuthenticationProvider.Cognito]: (params: AuthenticationTokenGetterParams) => CognitoTokenGetter.create(params),
	[AuthenticationProvider.Auth0]: (params: AuthenticationTokenGetterParams) => Auth0TokenGetter.create(params),
} as const satisfies Record<
	AuthenticationProvider,
	(params: AuthenticationTokenGetterParams) => IAuthenticationTokenGetter
>;

interface GetOpenapiFetchClientParams {
	readonly baseUrl: string;
}

export function getAuthenticationInfo({ baseUrl }: GetOpenapiFetchClientParams): GetAuthenticationInfoResponse {
	const authStore = getOrCreateAuthStore();

	const authenticationProvider = getAuthenticationProvider(authStore);
	const create = TOKEN_GETTER_CREATORS[authenticationProvider];
	const { headers, baseUrlWithAuthPath }: IAuthenticationTokenGetter = create({
		authStore,
		baseUrl,
	});

	return {
		headers,
		baseUrlWithAuthPath: baseUrlWithAuthPath,
	};
}
